"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.karyaDb = exports.karyaTableNames = void 0;
const Index_1 = require("./strings/Index");
// Definitive list of all karya database tables
exports.karyaTableNames = [
    'server_user',
    'box',
    'worker',
    'worker_monitoring',
    'project_group',
    'project',
    'user_project_acl',
    'avatar',
    'avatar_gen_bulk',
    'otp',
    'karya_file',
    'dataset',
    'datapoint',
    'dataset_op',
    'dataset_subscription',
    'task',
    'microtask_group',
    'microtask',
    'task_op',
    'task_assignment',
    'microtask_group_assignment',
    'microtask_assignment',
    'microtask_assignment_batch',
    'microtask_assignment_batch_member',
    'script_op',
    'referral',
    'task_survey',
    'additional_payment',
    'device_restriction',
    'ticket',
    'clients',
    'reports',
    'client_reports',
    'conferencer',
    'fund_account',
    'transaction',
    'bulk_transaction',
    'payment_transaction_jobs',
    'fund_account_restriction',
    'mta_completion_queue',
    'mta_verification_queue',
    'mt_completion_queue',
    'seva_user',
    'seva_user_tickets',
    'eligible_users_query',
    'form',
    'form_assignment',
    'form_trigger',
    'notification',
    'learn_task',
    'learn_worker_stats',
    'org_config',
    'org_payment_config',
    'payment_provider_account_configs',
    'bulk_transaction_task',
    'worker_task_payment',
    'worker_kyc',
    'worker_tds_eligibility',
    'worker_deletion_request',
    'worker_project_fy_earnings',
    'worker_project_fy_transfers',
];
// List of custom strings
const _karyaStrings = [
    'PaymentStatus',
    'PaymentMethod',
    'PaymentGatewayProvider',
    'FundAccountType',
    'FundAccountStatus',
    'TransactionPurpose',
    'BulkTransactionStatus',
    'PaymentTransactionJobStatus',
    'ServerRole',
    'ProjectRole',
    'ProjectStatus',
    'ProjectMigrationStatus',
    'ProjectPaymentMode',
    'ProjectGroupStatus',
    'OtpProvider',
    'CountryIsoCode',
    'CurrencyIsoCode',
    'RegistrationMechanism',
    'Gender',
    'ContainerName',
    'FileCreator',
    'ChecksumAlgorithm',
    'LanguageCode',
    'TaskOutputGranularity',
    'TaskStatus',
    'MicrotaskStatus',
    'TaskOpType',
    'TaskOpStatus',
    'TaskAssignmentStatus',
    'MicrotaskAssignmentStatus',
    'MicrotaskAssignmentBatchStatus',
    'DatapointStatus',
    'DatasetOpType',
    'OpStatus',
    'ReferralStatus',
    'AccountDeletionStatus',
    'WorkerJourneyState',
    'TicketType',
    'ConferencerCallStatus',
    'ConferencerAcknowledgementStatus',
    'FundAccountType',
    'FundAccountStatus',
    'PaymentGatewayProvider',
    'ConferencerCalleeCallResponse',
    'ConferencerProviderCallAction',
    'ReportKind',
    'ReportType',
    'WorkerCategory',
    'FormAssignmentStatus',
    'FormStatus',
    'FormScheduleType',
    'FormScheduleInterval',
    'FormTriggerType',
    'FormTriggerStatus',
    'NotificationStatus',
    'NotificationTriggerType',
    'NotificationType',
    'KycStatus',
    'KycDocumentType',
    'MatchResult',
    'ProjectVersion',
    'WorkPacketStatus',
    'WorkPacketAssignmentStatus',
    'WorkPacketGeneratorState',
];
// List of custom objects
const _karyaObjects = [
    'PropsObject',
    'PaymentAccountFieldDefs',
    'MicrotaskInput',
    'MicrotaskOutput',
    'CompletionPolicy',
    'AssignmentPolicy',
    'TestEvaluationPolicy',
    'DatasetProps',
    'DatasetSummaryStats',
    'DatapointProps',
    'DatasetShape',
    'TaskParams',
    'TaskSummaryStats',
    'ProjectProps',
    'AvatarSummary',
    'AvatarBulkSummary',
    'WorkerSummary',
    'DatasetSubscriptionFilter',
    'NgGenericTask',
    'Survey',
    'ReportQueryParameterList',
    'ReportConfig',
    'DataPacketFiles',
    'WorkPacketConfig',
    'WorkPacketAllocationStrategy',
    'WorkPacketValidationStrategy',
    'WorkPacketAssignmentFeedback',
    'WorkPacketAssignmentValidationResult',
];
// Karya Database Specification
const karyaDb = {
    version: '2.0.0',
    tables: {
        mt_completion_queue: {
            columns: [
                ['mt_id', ['>', 'microtask'], 'unique', 'not nullable', 'not mutable', true],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        mta_verification_queue: {
            columns: [
                ['mta_id', ['>', 'microtask_assignment'], 'unique', 'not nullable', 'not mutable', true],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        mta_completion_queue: {
            columns: [
                ['mta_id', ['>', 'microtask_assignment'], 'unique', 'not nullable', 'not mutable', true],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        server_user: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['access_code', ['string', 32], 'unique', 'not nullable', 'not mutable'],
                ['registered', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                [
                    'reg_mechanism',
                    ['string', 32, 'RegistrationMechanism', undefined, Index_1.registrationMechanisms],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['phone_number', ['string', 16], 'not unique', 'nullable', 'mutable'],
                ['otp', ['string', 8], 'not unique', 'nullable', 'mutable'],
                ['otp_generated_at', ['stringarray'], 'not unique', 'nullable', 'mutable'],
                ['auth_id', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['id_token', ['text'], 'unique', 'nullable', 'mutable'],
                ['role', ['string', 32, 'ServerRole', undefined, Index_1.serverRoles], 'not unique', 'not nullable', 'mutable'],
                ['role_mappings', ['stringarray'], 'not unique', 'nullable', 'mutable'],
                ['full_name', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['email', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        box: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['access_code', ['string', 32], 'unique', 'not nullable', 'not mutable'],
                ['registered', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                [
                    'reg_mechanism',
                    ['string', 32, 'RegistrationMechanism', undefined, Index_1.registrationMechanisms],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['key', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['physical', ['boolean', false], 'not unique', 'not nullable', 'not mutable'],
                ['name', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['location', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['gps', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['url', ['string', 256], 'not unique', 'nullable', 'mutable'],
                ['auth_id', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['id_token', ['text'], 'unique', 'nullable', 'mutable'],
                ['last_received_from_server_at', ['timestamp', 'eon'], 'not unique', 'not nullable', 'mutable'],
                ['last_sent_to_server_at', ['timestamp', 'eon'], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        worker: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['phone_number', ['string', 12], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'category',
                    ['string', 20, 'WorkerCategory', 'WORKER', Index_1.WorkerCategories],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['full_name', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['gender', ['string', 15], 'not unique', 'nullable', 'mutable', true],
                ['pincode', ['string', 16], 'not unique', 'nullable', 'mutable', true],
                ['yob', ['int'], 'not unique', 'nullable', 'mutable', true],
                ['profile', ['kv'], 'not unique', 'not nullable', 'mutable'],
                ['profile_updated_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['id_token', ['text'], 'unique', 'nullable', 'mutable'],
                ['tags', ['stringarray'], 'not unique', 'not nullable', 'mutable'],
                ['summary', ['object', 'WorkerSummary'], 'not unique', 'not nullable', 'mutable'],
                ['fcm_token', ['string', 256], 'not unique', 'nullable', 'mutable'],
                ['pan_verified_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['preferences', ['object'], 'not unique', 'nullable', 'mutable'],
                ['disabled', ['boolean'], 'not unique', 'nullable', 'mutable'],
                ['disabled_reason', ['text'], 'not unique', 'nullable', 'mutable'],
                ['kyc_retry_count', ['int', 0], 'not unique', 'not nullable', 'mutable'],
                ['org_id', ['>', 'org_config'], 'not unique', 'not nullable', 'not mutable', true],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        // Self-serve account deletion. One row per worker (worker_id unique): the current/most-recent
        // deletion request. status: 'PENDING' (recovery window open) | 'CANCELLED' (restored) |
        // 'DELETED' (PII purged). completed_at is set when the request reaches a terminal state.
        worker_deletion_request: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                // Not unique: a worker may delete again after restoring a previous request. Always read the
                // most recent row (ordered by created_at) — see WorkerModel.getLatestDeletionRequest.
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['requested_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                // Defaults to PENDING so a new request need not set it explicitly; moves to CANCELLED / DELETED.
                [
                    'status',
                    ['string', 20, 'AccountDeletionStatus', 'PENDING', Index_1.accountDeletionStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['completed_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['reason', ['text'], 'not unique', 'nullable', 'not mutable'],
                // Free-text elaboration the client collects when the worker picks the "Other" reason. Kept
                // separate from `reason` so that column stays a clean machine-readable code for analytics.
                ['reason_details', ['text'], 'not unique', 'nullable', 'not mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [
                ['worker_id', 'created_at'],
                ['status', 'requested_at'],
            ],
        },
        worker_monitoring: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'nullable', 'not mutable', true],
                ['data', ['object'], 'not unique', 'not nullable', 'mutable'],
                ['day', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        project_group: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['code_name', ['string', 128], 'unique', 'not nullable', 'mutable', false],
                ['name', ['string', 128], 'not unique', 'not nullable', 'mutable', false],
                ['description', ['text'], 'not unique', 'nullable', 'mutable', false],
                [
                    'status',
                    ['string', 16, 'ProjectGroupStatus', 'ACTIVE', Index_1.projectGroupStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                    false,
                ],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        project: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['code_name', ['string', 128], 'unique', 'nullable', 'mutable', false],
                ['name', ['string', 128], 'not unique', 'not nullable', 'mutable', false],
                ['description', ['text'], 'not unique', 'nullable', 'mutable', false],
                ['props', ['object', 'ProjectProps'], 'not unique', 'nullable', 'mutable', false],
                [
                    'status',
                    ['string', 16, 'ProjectStatus', undefined, Index_1.projectStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                    false,
                ],
                [
                    'migration_status',
                    ['string', 16, 'ProjectMigrationStatus', 'NOT_MIGRATED', Index_1.projectMigrationStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                    true,
                ],
                ['payments_enabled', ['boolean', false], 'not unique', 'nullable', 'not mutable', true],
                [
                    'payment_mode',
                    ['string', 16, 'ProjectPaymentMode', 'PROJECT', Index_1.projectPaymentModes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                [
                    'version',
                    ['string', 4, 'ProjectVersion', 'V1', Index_1.projectVersions],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['project_group_id', ['>', 'project_group'], 'not unique', 'nullable', 'mutable', true],
                ['org_id', ['>', 'org_config'], 'not unique', 'not nullable', 'not mutable', true],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        user_project_acl: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['user_id', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['role', ['string', 32, 'ProjectRole', undefined, Index_1.projectRoles], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        avatar: {
            columns: [
                ['id', ['bigint'], 'unique', 'not nullable', 'not mutable'],
                ['box_id', ['>', 'box'], 'not unique', 'not nullable', 'not mutable'],
                ['local_id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['bulk_id', ['>', 'avatar_gen_bulk'], 'not unique', 'nullable', 'not mutable', true],
                ['access_code', ['string', 32], 'unique', 'not nullable', 'not mutable'],
                ['config', ['kv'], 'not unique', 'nullable', 'mutable'],
                ['task_id', ['>', 'task'], 'not unique', 'nullable', 'not mutable', true],
                ['test_passed', ['boolean'], 'not unique', 'nullable', 'mutable'],
                ['tags', ['stringarray'], 'not unique', 'not nullable', 'mutable'],
                ['tags_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
                ['is_disabled', ['boolean', false], 'not unique', 'not nullable', 'mutable', true],
                ['is_payments_disabled', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['sent_to_server_at', ['timestamp', 'eon'], 'not unique', 'not nullable', 'mutable'],
                ['is_linked', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['linked_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'nullable', 'mutable', true],
                ['test', ['boolean', false], 'not unique', 'not nullable', 'not mutable'],
                ['summary', ['object', 'AvatarSummary'], 'not unique', 'not nullable', 'mutable'],
                [
                    'journey_state',
                    ['string', 30, 'WorkerJourneyState', 'MAIN_TASK', Index_1.WorkerJourneyStates],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['logs', ['kv'], 'not unique', 'nullable', 'mutable'],
                ['linked_device_id', ['string', 32], 'not unique', 'nullable', 'mutable', true],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable', true],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['project_id', 'is_disabled']],
        },
        avatar_gen_bulk: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['name', ['string', 100], 'not unique', 'nullable', 'mutable'],
                ['box_id', ['>', 'box'], 'not unique', 'not nullable', 'not mutable'],
                ['tags', ['stringarray'], 'not unique', 'not nullable', 'mutable'],
                ['task_id', ['>', 'task'], 'not unique', 'nullable', 'not mutable'],
                ['summary', ['object', 'AvatarBulkSummary'], 'not unique', 'not nullable', 'mutable'],
                ['last_generated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        otp: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['phone_number', ['string', 12], 'not unique', 'not nullable', 'not mutable', true],
                ['otp_id', ['string', 48], 'not unique', 'not nullable', 'not mutable'],
                ['value', ['string', 12], 'not unique', 'not nullable', 'not mutable'],
                ['status', ['string', 16], 'not unique', 'not nullable', 'mutable'],
                ['generated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['sent_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['resent_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['verified_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        karya_file: {
            columns: [
                ['id', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['box_id', ['>', 'box'], 'not unique', 'nullable', 'not mutable'],
                ['local_id', ['bigserial'], 'not unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'container_name',
                    ['string', 64, 'ContainerName', undefined, Index_1.containerNames],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['name', ['string', 512], 'not unique', 'not nullable', 'not mutable', true],
                ['orig_name', ['string', 512], 'not unique', 'nullable', 'mutable'],
                ['url', ['string', 1024], 'not unique', 'nullable', 'mutable'],
                [
                    'creator',
                    ['string', 16, 'FileCreator', undefined, Index_1.fileCreators],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['creator_id', ['bigint'], 'not unique', 'not nullable', 'not mutable'],
                ['timestamp', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                [
                    'algorithm',
                    ['string', 8, 'ChecksumAlgorithm', undefined, Index_1.checksumAlgorithms],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['checksum', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['in_box', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['in_server', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        dataset: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['dtype', ['string', 32], 'not unique', 'not nullable', 'not mutable'],
                ['name', ['string', 128], 'not unique', 'not nullable', 'mutable'],
                ['source', ['kv'], 'not unique', 'not nullable', 'mutable'],
                ['props', ['object', 'DatasetProps'], 'not unique', 'not nullable', 'mutable'],
                ['summary', ['object', 'DatasetSummaryStats'], 'not unique', 'not nullable', 'mutable'],
                ['dataset_shape', ['object', 'DatasetShape'], 'not unique', 'nullable', 'mutable'],
                ['is_gold', ['boolean', false], 'not unique', 'not nullable', 'not mutable', true],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        datapoint: {
            columns: [
                ['id', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['dataset_id', ['>', 'dataset'], 'not unique', 'not nullable', 'not mutable', true],
                ['data', ['object'], 'not unique', 'not nullable', 'mutable'],
                ['files', ['stringdict'], 'not unique', 'not nullable', 'mutable'],
                ['file_id', ['>', 'karya_file'], 'not unique', 'nullable', 'mutable'],
                ['props', ['object', 'DatapointProps'], 'not unique', 'not nullable', 'mutable'],
                ['src_datapoint_id', ['>', 'datapoint'], 'not unique', 'nullable', 'not mutable', true],
                ['generated_by_test_task', ['boolean', false], 'not unique', 'not nullable', 'not mutable'],
                ['generated_by_mta_id', ['>', 'microtask_assignment'], 'unique', 'nullable', 'not mutable', true],
                [
                    'status',
                    ['string', 20, 'DatapointStatus', 'ACTIVE', Index_1.datapointStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                    true,
                ],
                ['is_gold', ['boolean', false], 'not unique', 'not nullable', 'not mutable', true],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        dataset_op: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['dataset_id', ['>', 'dataset'], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'op_type',
                    ['string', 32, 'DatasetOpType', undefined, Index_1.datasetOpTypes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                    true,
                ],
                ['ref_id', ['bigint'], 'not unique', 'nullable', 'not mutable'],
                ['file_id', ['>', 'karya_file'], 'unique', 'nullable', 'mutable'],
                ['status', ['string', 16, 'OpStatus', undefined, Index_1.taskOpStatuses], 'not unique', 'not nullable', 'mutable'],
                ['started_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['completed_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['message', ['text'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        dataset_subscription: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['dataset_id', ['>', 'dataset'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['filter', ['object', 'DatasetSubscriptionFilter'], 'not unique', 'nullable', 'not mutable'],
                ['status', ['string', 16], 'not unique', 'not nullable', 'mutable'],
                ['test', ['boolean', false], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['dataset_id', 'status']], // originally only partial index will be added which is on status='active'
        },
        task: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['work_provider_id', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable'],
                ['scenario_name', ['string', 32], 'not unique', 'not nullable', 'not mutable'],
                ['name', ['string', 128], 'not unique', 'not nullable', 'mutable'],
                ['description', ['text'], 'not unique', 'not nullable', 'mutable'],
                ['display_name', ['string', 1024], 'not unique', 'not nullable', 'mutable'],
                ['policy', ['string', 16], 'not unique', 'not nullable', 'not mutable'],
                ['params', ['object', 'TaskParams'], 'not unique', 'not nullable', 'mutable'],
                ['itags', ['stringarray'], 'not unique', 'not nullable', 'mutable'],
                ['otags', ['stringarray'], 'not unique', 'not nullable', 'mutable'],
                ['wgroup', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['deadline', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['assignment_granularity', ['string', 16], 'not unique', 'nullable', 'not mutable'],
                ['group_assignment_order', ['string', 16], 'not unique', 'nullable', 'mutable'],
                ['microtask_assignment_order', ['string', 16], 'not unique', 'nullable', 'mutable'],
                ['assignment_batch_size', ['int'], 'not unique', 'nullable', 'mutable'],
                ['test_evaluation_policy', ['object', 'TestEvaluationPolicy'], 'not unique', 'nullable', 'mutable'],
                ['completion_policy', ['object', 'CompletionPolicy'], 'not unique', 'not nullable', 'mutable'],
                ['status', ['string', 16, 'TaskStatus', undefined, Index_1.taskStatuses], 'not unique', 'not nullable', 'mutable'],
                ['summary', ['object', 'TaskSummaryStats'], 'not unique', 'nullable', 'mutable'],
                ['output_dataset_id', ['>', 'dataset'], 'not unique', 'nullable', 'mutable'],
                [
                    'output_gen_granularity',
                    ['string', 16, 'TaskOutputGranularity', undefined, Index_1.taskOutputGranularities],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['config_spec', ['object', 'NgGenericTask'], 'not unique', 'nullable', 'not mutable'],
                ['intro_spec', ['object', 'NgGenericTask'], 'not unique', 'nullable', 'not mutable'],
                ['tutorial_spec', ['object', 'NgGenericTask'], 'not unique', 'nullable', 'not mutable'],
                ['main_spec', ['object', 'NgGenericTask'], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        microtask_group: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['microtask_assignment_order', ['string', 16], 'not unique', 'not nullable', 'mutable'],
                [
                    'status',
                    ['string', 16, 'MicrotaskStatus', undefined, Index_1.microtaskStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                    true,
                ],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['task_id', 'status']],
        },
        microtask: {
            columns: [
                ['id', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['group_id', ['>', 'microtask_group'], 'not unique', 'nullable', 'not mutable'],
                [
                    'input',
                    ['object', 'MicrotaskInput', ['InputData', 'InputFiles']],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['input_file_id', ['>', 'karya_file'], 'not unique', 'nullable', 'not mutable'],
                ['input_dp_id', ['>', 'datapoint'], 'not unique', 'nullable', 'not mutable', true],
                ['deadline', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['base_credits', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['credits', ['int'], 'not unique', 'not nullable', 'mutable'],
                [
                    'status',
                    ['string', 16, 'MicrotaskStatus', undefined, Index_1.microtaskStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['output', ['object', 'MicrotaskOutput', ['OutputData', 'OutputFiles']], 'not unique', 'nullable', 'mutable'],
                ['output_dp_id', ['>', 'datapoint'], 'not unique', 'nullable', 'mutable'],
                ['test', ['boolean', false], 'not unique', 'not nullable', 'not mutable'],
                ['is_gold', ['boolean', false], 'not unique', 'not nullable', 'not mutable', true],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            templates: [
                ['InputData', 'object'],
                ['InputFiles', 'stringdict'],
                ['OutputData', 'object'],
                ['OutputFiles', 'stringdict'],
            ],
            indices: [['task_id', 'status']],
        },
        task_op: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'op_type',
                    ['string', 32, 'TaskOpType', undefined, Index_1.taskOpTypes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                    true,
                ],
                ['file_id', ['>', 'karya_file'], 'unique', 'nullable', 'not mutable'],
                ['status', ['string', 16, 'TaskOpStatus', undefined, Index_1.taskOpStatuses], 'not unique', 'not nullable', 'mutable'],
                ['started_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['completed_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['messages', ['stringarray'], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        task_assignment: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['box_id', ['>', 'box'], 'not unique', 'not nullable', 'not mutable'],
                ['assignment_policy', ['object', 'AssignmentPolicy'], 'not unique', 'not nullable', 'mutable'],
                ['policy', ['string', 16], 'not unique', 'not nullable', 'not mutable'],
                ['params', ['kv'], 'not unique', 'not nullable', 'not mutable'],
                ['deadline', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                [
                    'status',
                    ['string', 16, 'TaskAssignmentStatus', undefined, Index_1.taskAssignmentStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['received_from_server_at', ['timestamp', 'eon'], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        task_survey: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['avatar_id', ['>', 'avatar'], 'not unique', 'not nullable', 'not mutable', true],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['survey', ['object', 'Survey'], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        microtask_group_assignment: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['box_id', ['>', 'box'], 'not unique', 'not nullable', 'not mutable'],
                ['local_id', ['bigserial'], 'not unique', 'not nullable', 'not mutable'],
                ['group_id', ['>', 'microtask_group'], 'not unique', 'not nullable', 'not mutable'],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['avatar_id', ['>', 'avatar'], 'not unique', 'not nullable', 'not mutable', true],
                ['test', ['boolean', false], 'not unique', 'not nullable', 'not mutable'],
                [
                    'status',
                    ['string', 16, 'MicrotaskAssignmentStatus', undefined, Index_1.microtaskAssignmentStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['task_id', 'status']],
        },
        microtask_assignment: {
            columns: [
                ['id', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['box_id', ['>', 'box'], 'not unique', 'not nullable', 'not mutable'],
                ['local_id', ['bigint'], 'not unique', 'nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['batch_id', ['string', 32], 'not unique', 'nullable', 'not mutable', true],
                ['microtask_id', ['>', 'microtask'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['avatar_id', ['>', 'avatar'], 'not unique', 'not nullable', 'not mutable', true],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['test', ['boolean', false], 'not unique', 'not nullable', 'not mutable'],
                ['is_gold', ['boolean', false], 'not unique', 'not nullable', 'not mutable', true],
                ['wgroup', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['sent_to_server_at', ['timestamp', 'eon'], 'not unique', 'not nullable', 'mutable'],
                ['deadline', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                [
                    'status',
                    ['string', 16, 'MicrotaskAssignmentStatus', undefined, Index_1.microtaskAssignmentStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['completed_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['output', ['object', 'MicrotaskOutput', ['OutputData', 'OutputFiles']], 'not unique', 'nullable', 'mutable'],
                ['output_file_id', ['>', 'karya_file'], 'not unique', 'nullable', 'mutable'],
                ['logs', ['stringarray'], 'not unique', 'nullable', 'mutable'],
                ['submitted_to_box_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['submitted_to_server_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['verified_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['report', ['object'], 'not unique', 'nullable', 'mutable'],
                ['max_base_credits', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['base_credits', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['max_credits', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['credits', ['int'], 'not unique', 'nullable', 'mutable'],
                ['accepted', ['boolean'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            templates: [
                ['OutputData', 'object'],
                ['OutputFiles', 'stringdict'],
            ],
            indices: [
                ['task_id', 'status'],
                ['task_id', 'test', 'microtask_id', 'avatar_id', 'status'],
            ],
        },
        microtask_assignment_batch: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['size', ['int'], 'not unique', 'not nullable', 'not mutable'],
                [
                    'status',
                    ['string', 16, 'MicrotaskAssignmentBatchStatus', undefined, Index_1.microtaskAssignmentBatchStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                    true,
                ],
                ['claimed_avatar_id', ['>', 'avatar'], 'not unique', 'nullable', 'mutable'],
                ['claimed_worker_id', ['>', 'worker'], 'not unique', 'nullable', 'mutable'],
                ['claimed_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['task_id', 'status', 'id']],
        },
        microtask_assignment_batch_member: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['batch_id', ['>', 'microtask_assignment_batch'], 'not unique', 'not nullable', 'not mutable', true],
                ['microtask_id', ['>', 'microtask'], 'not unique', 'not nullable', 'not mutable', true],
                ['ord', ['int'], 'not unique', 'not nullable', 'not mutable'],
                ['released_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['batch_id', 'ord']],
        },
        script_op: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['script_id', ['string', 64], 'not unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable'],
                ['description', ['text'], 'not unique', 'nullable', 'not mutable'],
                ['params', ['kv'], 'not unique', 'not nullable', 'mutable'],
                ['status', ['string', 32, 'OpStatus', undefined, Index_1.taskOpStatuses], 'not unique', 'not nullable', 'mutable'],
                ['stdout', ['text'], 'not unique', 'nullable', 'mutable'],
                ['stderr', ['text'], 'not unique', 'nullable', 'mutable'],
                ['error', ['kv'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['script_id', 'status']],
        },
        referral: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['referred_task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable'],
                ['referred_phone_number', ['string', 12], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'status',
                    ['string', 16, 'ReferralStatus', undefined, Index_1.referralStatuses],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['failure_reason', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['worker_id', 'referred_phone_number']],
        },
        additional_payment: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['amount', ['int'], 'not unique', 'not nullable', 'not mutable'],
                ['description', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['added_by', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        device_restriction: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['device_id', ['string', 256], 'not unique', 'not nullable', 'mutable'],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable'],
                ['allowed_users', ['int'], 'not unique', 'nullable', 'mutable'],
                ['description', ['string', 64], 'not unique', 'nullable', 'mutable'],
                ['added_by', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['device_id', 'task_id']],
        },
        fund_account_restriction: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['credentials', ['string', 256], 'not unique', 'not nullable', 'mutable', true],
                ['no_of_users_per_account', ['int'], 'not unique', 'nullable', 'mutable'],
                ['description', ['text'], 'not unique', 'nullable', 'mutable'],
                ['added_by', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        /**
         * Note: This table is not in prod yet but a ticket feature is using it, so keeping it for now .
         * @field ticket_id : Unique ticket id from third party
         **/
        ticket: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['provider_ticket_id', ['string', 256], 'unique', 'not nullable', 'not mutable', true],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['category', ['string', 32, 'TicketType', undefined, Index_1.ticketTypes], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        clients: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['name', ['text'], 'not unique', 'not nullable', 'mutable'],
                ['api_key', ['text'], 'unique', 'not nullable', 'mutable', true],
                ['id_token', ['text'], 'unique', 'not nullable', 'mutable', true], // This is just dummy placeholder to maintain compatibility with other tables regarding Auth. Originally we don't have this field in clients table.
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        reports: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['kind', ['string', 32, 'ReportKind', undefined, Index_1.ReportKinds], 'not unique', 'not nullable', 'not mutable'],
                ['name', ['text'], 'not unique', 'not nullable', 'mutable'],
                ['type', ['string', 16, 'ReportType', undefined, Index_1.ReportTypes], 'not unique', 'not nullable', 'mutable'],
                ['sql_query', ['text'], 'not unique', 'not nullable', 'mutable'],
                ['config', ['object', 'ReportConfig'], 'not unique', 'not nullable', 'mutable'],
                ['is_public', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['is_disabled', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['is_clickhouse_query', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['created_by', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_by', ['>', 'server_user'], 'not unique', 'not nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        client_reports: {
            columns: [
                ['client_id', ['>', 'clients'], 'not unique', 'not nullable', 'not mutable'],
                ['report_id', ['>', 'reports'], 'not unique', 'not nullable', 'not mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['client_id', 'report_id']],
        },
        conferencer: {
            columns: [
                ['id', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['caller_number', ['string', 13], 'not unique', 'not nullable', 'not mutable', true],
                ['callee_number', ['string', 13], 'not unique', 'not nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['caller_mta', ['>', 'microtask_assignment'], 'not unique', 'not nullable', 'not mutable'],
                ['callee_mta', ['>', 'microtask_assignment'], 'unique', 'not nullable', 'not mutable', true],
                ['meta', ['kv'], 'not unique', 'nullable', 'mutable'],
                [
                    'acknowledgement_status',
                    ['string', 16, 'ConferencerAcknowledgementStatus', undefined, Index_1.conferencerAcknowledgementStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                [
                    'call_status',
                    ['string', 24, 'ConferencerCallStatus', undefined, Index_1.conferencerCallStatuses],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                [
                    'callee_response_to_call',
                    ['string', 16, 'ConferencerCalleeCallResponse', undefined, Index_1.conferencerCalleeCallResponses],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                [
                    'provider_action_on_call',
                    ['string', 24, 'ConferencerProviderCallAction', undefined, Index_1.conferencerProviderCallActions],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['participants_in_room', ['int'], 'not unique', 'nullable', 'mutable'],
                ['caller_start_call_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['callee_response_to_call_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['provider_action_on_call_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['caller_rejected_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['caller_hangup_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['callee_hangup_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['recording_meta', ['kv'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        fund_account: {
            columns: [
                ['id', ['bigint'], 'unique', 'not nullable', 'not mutable'],
                ['user_id', ['>', 'worker'], 'unique', 'not nullable', 'not mutable', true],
                ['credentials', ['string', 50], 'not unique', 'not nullable', 'mutable'],
                [
                    'account_type',
                    ['string', 32, 'FundAccountType', undefined, Index_1.fundAccountTypes],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                [
                    'status',
                    ['string', 32, 'FundAccountStatus', undefined, Index_1.fundAccountStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['verified_count', ['int', 0], 'not unique', 'not nullable', 'mutable'],
                ['transaction_in_progress', ['boolean'], 'not unique', 'not nullable', 'mutable'],
                ['props', ['object', 'PropsObject'], 'not unique', 'not nullable', 'mutable'],
                ['meta', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        transaction: {
            columns: [
                ['id', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['amount', ['int'], 'not unique', 'not nullable', 'not mutable'],
                ['tds_amount', ['int', 0], 'not unique', 'not nullable', 'not mutable'],
                [
                    'status',
                    ['string', 32, 'PaymentStatus', undefined, Index_1.paymentStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                [
                    'purpose',
                    ['string', 32, 'TransactionPurpose', undefined, Index_1.transactionPurposes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                [
                    'payment_method',
                    ['string', 32, 'PaymentMethod', undefined, Index_1.paymentMethods],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                [
                    'payment_gateway_provider',
                    ['string', 32, 'PaymentGatewayProvider', undefined, Index_1.paymentGatewayProviders],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['financial_year', ['string', 10], 'not unique', 'not nullable', 'not mutable'],
                ['provider_transaction_id', ['string', 50], 'unique', 'nullable', 'mutable'],
                ['fund_account_id', ['>', 'fund_account'], 'not unique', 'not nullable', 'not mutable', true],
                ['user_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                // Sentinel 0 = unknown/unscoped. Not a FK to project.
                ['project_id', ['bigint', 0], 'not unique', 'not nullable', 'not mutable', true],
                ['bulk_transaction_id', ['>', 'bulk_transaction'], 'not unique', 'nullable', 'not mutable'],
                ['idempotency_key', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['created_by_user_id', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable'],
                ['props', ['object', 'PropsObject'], 'not unique', 'not nullable', 'mutable'],
                ['meta', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        bulk_transaction: {
            columns: [
                ['id', ['string', 50], 'unique', 'not nullable', 'not mutable'],
                ['amount', ['int'], 'not unique', 'not nullable', 'not mutable'],
                [
                    'status',
                    ['string', 30, 'BulkTransactionStatus', undefined, Index_1.bulkTransactionStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['n_transactions', ['int'], 'not unique', 'not nullable', 'not mutable'],
                ['financial_year', ['string', 10], 'not unique', 'not nullable', 'not mutable'],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['created_by_user_id', ['>', 'server_user'], 'not unique', 'not nullable', 'not mutable'],
                ['props', ['object', 'PropsObject'], 'not unique', 'not nullable', 'mutable'],
                ['meta', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        payment_transaction_jobs: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                [
                    'status',
                    ['string', 16, 'PaymentTransactionJobStatus', 'CREATED', Index_1.paymentTransactionJobStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                    true,
                ],
                ['payload', ['object'], 'not unique', 'not nullable', 'not mutable'],
                ['picked_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['completed_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        seva_user: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                // refers to whatsapp_id for whatsapp/turn.io chatbot
                ['messaging_id', ['string', 100], 'unique', 'not nullable', 'not mutable', true],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        seva_user_tickets: {
            columns: [
                ['karya_ticket_id', ['string', 50], 'unique', 'not nullable', 'not mutable', true],
                ['seva_user_id', ['>', 'seva_user'], 'not unique', 'not nullable', 'not mutable'],
                ['provider_ticket_id', ['string', 50], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        eligible_users_query: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['name', ['string', 128], 'not unique', 'not nullable', 'mutable'],
                ['query', ['text'], 'not unique', 'not nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        form: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['name', ['string', 128], 'not unique', 'not nullable', 'mutable'],
                ['status', ['string', 16, 'FormStatus', undefined, Index_1.formStatuses], 'not unique', 'not nullable', 'mutable'],
                ['content', ['object'], 'not unique', 'nullable', 'mutable'],
                ['spec', ['object'], 'not unique', 'not nullable', 'mutable'],
                ['expiry', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                [
                    'schedule_type',
                    ['string', 16, 'FormScheduleType', undefined, Index_1.formScheduleTypes],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['schedule_fixed_date', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                [
                    'schedule_periodic_interval',
                    ['string', 16, 'FormScheduleInterval', undefined, Index_1.formScheduleIntervals],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['eligible_users_query_id', ['>', 'eligible_users_query'], 'not unique', 'not nullable', 'not mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        form_assignment: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['form_id', ['>', 'form'], 'not unique', 'not nullable', 'not mutable', true],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'status',
                    ['string', 16, 'FormAssignmentStatus', undefined, Index_1.formAssignmentStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['output', ['object'], 'not unique', 'nullable', 'mutable'],
                ['expires_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['assigned_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['submitted_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['form_id', 'worker_id']],
        },
        form_trigger: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['form_id', ['>', 'form'], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'trigger_type',
                    ['string', 16, 'FormTriggerType', undefined, Index_1.formTriggerTypes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                [
                    'status',
                    ['string', 16, 'FormTriggerStatus', undefined, Index_1.formTriggerStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['last_run_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
                ['completed_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['message', ['text'], 'not unique', 'nullable', 'mutable'],
                ['eligibility_query_result_file', ['text'], 'not unique', 'nullable', 'mutable'],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['form_id', 'trigger_type']],
        },
        notification: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable'],
                ['title', ['string', 256], 'not unique', 'not nullable', 'not mutable'],
                ['body', ['text'], 'not unique', 'not nullable', 'not mutable'],
                [
                    'status',
                    ['string', 16, 'NotificationStatus', undefined, Index_1.notificationStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['navigation_link', ['string', 512], 'not unique', 'nullable', 'not mutable'],
                ['has_action', ['boolean'], 'not unique', 'not nullable', 'not mutable'],
                [
                    'trigger_type',
                    ['string', 16, 'NotificationTriggerType', undefined, Index_1.notificationTriggerTypes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                [
                    'type',
                    ['string', 32, 'NotificationType', undefined, Index_1.notificationTypes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['extras', ['object'], 'not unique', 'nullable', 'mutable'],
                ['seen_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['opened_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['delivered_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['worker_id', 'created_at']],
        },
        learn_task: {
            columns: [
                ['task_id', ['>', 'task'], 'unique', 'not nullable', 'not mutable', true],
                ['stars', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['duration_min', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['duration_max', ['int'], 'not unique', 'not nullable', 'mutable'],
                ['language_requests', ['object'], 'not unique', 'nullable', 'mutable'],
                ['retry_delay_days', ['int'], 'not unique', 'nullable', 'mutable'],
                ['max_task_retries', ['int'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        learn_worker_stats: {
            columns: [
                ['worker_id', ['>', 'worker'], 'unique', 'not nullable', 'not mutable', true],
                ['total_stars', ['int', 0], 'not unique', 'not nullable', 'mutable'],
                ['practice_task_attempts', ['object'], 'not unique', 'nullable', 'mutable'],
                ['retry_tracking', ['object'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        org_config: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['org_code', ['string', 32], 'unique', 'not nullable', 'mutable'],
                ['org_name', ['string', 128], 'not unique', 'not nullable', 'mutable'],
                ['profile_task_id', ['>', 'task'], 'not unique', 'not nullable', 'mutable'],
                ['require_profile_validation', ['boolean', false], 'not unique', 'not nullable', 'mutable'],
                ['tds_enabled', ['boolean', true], 'not unique', 'not nullable', 'mutable'],
                [
                    'otp_provider',
                    ['string', 16, 'OtpProvider', undefined, Index_1.otpProviders],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['default_country_iso', ['string', 2, 'CountryIsoCode'], 'not unique', 'not nullable', 'mutable'],
                ['consent_markdown_link', ['string', 1024], 'not unique', 'nullable', 'mutable'],
                ['hrs_markdown_link', ['string', 1024], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        org_payment_config: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['org_id', ['>', 'org_config'], 'unique', 'not nullable', 'not mutable'],
                [
                    'payment_provider',
                    ['string', 16, 'PaymentGatewayProvider', undefined, Index_1.paymentGatewayProviders],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                ['currency', ['string', 3, 'CurrencyIsoCode'], 'not unique', 'not nullable', 'mutable'],
                ['credits_per_currency_unit', ['int', 1], 'not unique', 'not nullable', 'mutable'],
                ['credit_unit_label', ['string', 32, undefined, 'Paise'], 'not unique', 'not nullable', 'not mutable'],
                ['payout_unit_label', ['string', 32, undefined, 'Rupees'], 'not unique', 'not nullable', 'not mutable'],
                ['currency_symbol', ['string', 8, undefined, '₹'], 'not unique', 'not nullable', 'not mutable'],
                ['min_payout_limit', ['int', 1], 'not unique', 'not nullable', 'mutable'],
                ['max_payout_limit', ['int', 30000], 'not unique', 'not nullable', 'mutable'],
                ['verification_amount', ['int', 200], 'not unique', 'not nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
        },
        payment_provider_account_configs: {
            columns: [
                [
                    'payment_provider',
                    ['string', 16, 'PaymentGatewayProvider', undefined, Index_1.paymentGatewayProviders],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                [
                    'account_type',
                    ['string', 32, 'FundAccountType', undefined, Index_1.fundAccountTypes],
                    'not unique',
                    'not nullable',
                    'not mutable',
                ],
                ['fields', ['object', 'PaymentAccountFieldDefs'], 'not unique', 'not nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            triggers: ['ALTER TABLE payment_provider_account_configs ADD PRIMARY KEY (payment_provider, account_type)'],
        },
        bulk_transaction_task: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['bulk_transaction_id', ['>', 'bulk_transaction'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['n_workers', ['int'], 'not unique', 'not nullable', 'not mutable'],
                ['total_amount', ['int'], 'not unique', 'not nullable', 'not mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['bulk_transaction_id', 'task_id']],
        },
        worker_task_payment: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['task_id', ['>', 'task'], 'not unique', 'not nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['transaction_id', ['>', 'transaction'], 'not unique', 'not nullable', 'not mutable', true],
                ['bulk_transaction_id', ['>', 'bulk_transaction'], 'not unique', 'not nullable', 'not mutable', true],
                ['amount', ['int'], 'not unique', 'not nullable', 'not mutable'],
                ['tds_amount', ['int', 0], 'not unique', 'not nullable', 'not mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['worker_id', 'task_id']],
        },
        worker_kyc: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                [
                    'kyc_status',
                    ['string', 16, 'KycStatus', 'NOT_STARTED', Index_1.kycStatuses],
                    'not unique',
                    'not nullable',
                    'mutable',
                ],
                [
                    'document_type',
                    ['string', 8, 'KycDocumentType', undefined, Index_1.kycDocumentTypes],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['provider_session_id', ['text'], 'not unique', 'nullable', 'mutable'],
                ['provider_form_link', ['text'], 'not unique', 'nullable', 'mutable'],
                ['session_expires_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['name_from_kyc', ['text'], 'not unique', 'nullable', 'mutable'],
                ['dob_from_kyc', ['text'], 'not unique', 'nullable', 'mutable'],
                ['gender_from_kyc', ['text'], 'not unique', 'nullable', 'mutable'],
                ['address_from_kyc', ['object'], 'not unique', 'nullable', 'mutable'],
                ['document_verification_details', ['object'], 'not unique', 'nullable', 'mutable'],
                ['document_number_hash', ['text'], 'not unique', 'nullable', 'mutable'],
                ['document_number_hash_reliable', ['boolean'], 'not unique', 'nullable', 'mutable'],
                ['document_number_encrypted', ['text'], 'not unique', 'nullable', 'mutable'],
                [
                    'name_match_result',
                    ['string', 8, 'MatchResult', undefined, Index_1.matchResults],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['name_similarity_score', ['float'], 'not unique', 'nullable', 'mutable'],
                [
                    'gender_match_result',
                    ['string', 8, 'MatchResult', undefined, Index_1.matchResults],
                    'not unique',
                    'nullable',
                    'mutable',
                ],
                ['consent_captured_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['consent_content', ['text'], 'not unique', 'nullable', 'mutable'],
                ['failure_reason', ['text'], 'not unique', 'nullable', 'mutable'],
                ['verified_at', ['timestamp'], 'not unique', 'nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['worker_id', 'created_at']],
        },
        worker_tds_eligibility: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['financial_year', ['string', 16], 'not unique', 'not nullable', 'not mutable', true],
                ['tds_applicable', ['boolean', false], 'not unique', 'not nullable', 'mutable', true],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [['worker_id', 'financial_year']],
        },
        /**
         * Archive-time HOT snapshot of per-worker FY earnings for a project.
         * Dual-read: non-ARCHIVED project detail + these rows for archived projects.
         */
        worker_project_fy_earnings: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['financial_year', ['string', 10], 'not unique', 'not nullable', 'not mutable'],
                ['total_earnings', ['bigint', 0], 'not unique', 'not nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [
                ['worker_id', 'project_id', 'financial_year'],
                ['project_id', 'financial_year'],
                ['worker_id', 'financial_year'],
            ],
        },
        /**
         * Archive-time HOT snapshot of per-worker FY transfers + TDS for a project.
         * Dual-read: non-ARCHIVED project detail + these rows for archived projects.
         */
        worker_project_fy_transfers: {
            columns: [
                ['id', ['bigserial'], 'unique', 'not nullable', 'not mutable'],
                ['worker_id', ['>', 'worker'], 'not unique', 'not nullable', 'not mutable', true],
                ['project_id', ['>', 'project'], 'not unique', 'not nullable', 'not mutable', true],
                ['financial_year', ['string', 10], 'not unique', 'not nullable', 'not mutable'],
                ['total_transferred', ['bigint', 0], 'not unique', 'not nullable', 'mutable'],
                ['total_tds_deducted', ['bigint', 0], 'not unique', 'not nullable', 'mutable'],
                ['created_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'not mutable'],
                ['last_updated_at', ['timestamp', 'now'], 'not unique', 'not nullable', 'mutable'],
            ],
            indices: [
                ['worker_id', 'project_id', 'financial_year'],
                ['project_id', 'financial_year'],
                ['worker_id', 'financial_year'],
            ],
        },
    },
};
exports.karyaDb = karyaDb;
// Compute ID triggers
const computeIdFunction = `CREATE OR REPLACE FUNCTION compute_id()
  RETURNS TRIGGER AS $$
  BEGIN
  IF NEW.id IS NOT NULL THEN
  RETURN NEW;
  END IF;
  IF NEW.box_id IS NULL THEN
  NEW.id = 0;
  ELSE
  NEW.id = NEW.box_id;
  END IF;
  NEW.id = (NEW.id << 48) + NEW.local_id;
  RETURN NEW;
  END;
  $$ language 'plpgsql'`;
// Dataset summary update function to update dp_count in dataset summary when datapoints are inserted or deleted
const updateDatasetSummaryOnDpChangeFunction = `CREATE OR REPLACE FUNCTION public.update_dataset_summary_on_dp_change()
 RETURNS trigger
 LANGUAGE plpgsql
AS $function$
DECLARE
  modified_dataset_ids BIGINT[];
  modified_dataset_id BIGINT;
  dp_count INT;
BEGIN
  -- Collect unique task_ids affected by the statement operation
  IF TG_OP = 'DELETE' THEN
    SELECT ARRAY_AGG(DISTINCT dataset_id) INTO modified_dataset_ids FROM old;
  ELSE
    SELECT ARRAY_AGG(DISTINCT dataset_id) INTO modified_dataset_ids FROM new;
  END IF;

  -- ARRAY_AGG returns NULL when no rows were affected; FOREACH over NULL raises an error
  IF modified_dataset_ids IS NULL THEN
    RETURN NULL;
  END IF;

  -- Loop through each affected dataset_id and update dataset summary
  FOREACH modified_dataset_id IN ARRAY modified_dataset_ids LOOP
    -- Skip NULL task_ids
    IF modified_dataset_id IS NULL THEN
      CONTINUE;
    END IF;

    -- Calculate dp_count (number of datapoints for this dataset)
    SELECT COUNT(*) INTO dp_count
    FROM datapoint
    WHERE dataset_id = modified_dataset_id
      AND status = 'ACTIVE';

    -- Update the dataset summary column
    UPDATE dataset
    SET summary = summary || jsonb_build_object(
      'dp_count', dp_count
    )
    WHERE id = modified_dataset_id;
  END LOOP;

  RETURN NULL;  -- Triggers must return a value
END;
$function$`;
const updateTaskSummaryOnMtChangeFunction = `
CREATE OR REPLACE FUNCTION public.update_task_summary_on_mt_change()
 RETURNS trigger
 LANGUAGE plpgsql
AS $function$
DECLARE
    modified_task_ids BIGINT[];
    modified_task_id BIGINT;
    mt_count INT;
BEGIN
    -- Collect unique task_ids affected by the statement operation
    IF TG_OP = 'DELETE' THEN
        SELECT ARRAY_AGG(DISTINCT task_id) INTO modified_task_ids FROM old;
    ELSE
        SELECT ARRAY_AGG(DISTINCT task_id) INTO modified_task_ids FROM new;
    END IF;

    -- ARRAY_AGG returns NULL when no rows were affected; FOREACH over NULL raises an error
    IF modified_task_ids IS NULL THEN
        RETURN NULL;
    END IF;

    -- Loop through each affected task_id and update task summary
    FOREACH modified_task_id IN ARRAY modified_task_ids LOOP
        -- Skip NULL task_ids
        IF modified_task_id IS NULL THEN
            CONTINUE;
        END IF;

        -- Calculate mt_count (number of microtasks for this task)
        SELECT COUNT(*) INTO mt_count
        FROM microtask
        WHERE task_id = modified_task_id;

        -- Update the task summary column
        UPDATE task
        SET summary = summary || jsonb_build_object(
            'mt_count', mt_count
        )
        WHERE id = modified_task_id;
    END LOOP;

    RETURN NULL;  -- Triggers must return a value
END;
$function$
`;
// Trigger function to update task summary when microtask_assignment rows are inserted, updated, or deleted
// NOTE: The NULL check is critical — ARRAY_AGG returns NULL when 0 rows are affected,
// and FOREACH over NULL raises a PL/pgSQL runtime error.
const updateTaskSummaryOnMtaChangeFunction = `CREATE OR REPLACE FUNCTION public.update_task_summary_on_mta_change()
 RETURNS trigger
 LANGUAGE plpgsql
AS $$
DECLARE
    modified_task_ids BIGINT[];
    modified_task_id BIGINT;
    mta_count INT;
BEGIN
    IF TG_OP = 'DELETE' THEN
        SELECT ARRAY_AGG(DISTINCT task_id) INTO modified_task_ids FROM old;
    ELSE
        SELECT ARRAY_AGG(DISTINCT task_id) INTO modified_task_ids FROM new;
    END IF;

    -- ARRAY_AGG returns NULL when no rows were affected; FOREACH over NULL raises an error
    IF modified_task_ids IS NULL THEN
        RETURN NULL;
    END IF;

    FOREACH modified_task_id IN ARRAY modified_task_ids LOOP
        IF modified_task_id IS NULL THEN
            CONTINUE;
        END IF;

        SELECT COUNT(*) INTO mta_count
        FROM microtask_assignment
        WHERE task_id = modified_task_id;

        UPDATE task
        SET summary = summary || jsonb_build_object(
            'mta_count', mta_count
        )
        WHERE id = modified_task_id;
    END LOOP;

    RETURN NULL;
END;
$$`;
// Trigger function to update task summary when avatar rows are inserted, updated, or deleted
// NOTE: The NULL check is critical — ARRAY_AGG returns NULL when 0 rows are affected,
// and FOREACH over NULL raises a PL/pgSQL runtime error.
const updateTaskSummaryOnAvatarChangeFunction = `CREATE OR REPLACE FUNCTION public.update_task_summary_on_avatar_change()
 RETURNS trigger
 LANGUAGE plpgsql
AS $$
DECLARE
    modified_task_ids BIGINT[];
    modified_task_id BIGINT;
    a_count INT;
BEGIN
    IF TG_OP = 'DELETE' THEN
        SELECT ARRAY_AGG(DISTINCT task_id) INTO modified_task_ids FROM old;
    ELSE
        SELECT ARRAY_AGG(DISTINCT task_id) INTO modified_task_ids FROM new;
    END IF;

    -- ARRAY_AGG returns NULL when no rows were affected; FOREACH over NULL raises an error
    IF modified_task_ids IS NULL THEN
        RETURN NULL;
    END IF;

    FOREACH modified_task_id IN ARRAY modified_task_ids LOOP
        IF modified_task_id IS NULL THEN
            CONTINUE;
        END IF;

        SELECT COUNT(*) INTO a_count
        FROM avatar
        WHERE task_id = modified_task_id;

        UPDATE task
        SET summary = summary || jsonb_build_object(
            'avatar_count', a_count
        )
        WHERE id = modified_task_id;
    END LOOP;

    RETURN NULL;
END;
$$`;
karyaDb.functions = [
    ['compute_id', computeIdFunction],
    ['update_dataset_summary_on_dp_change', updateDatasetSummaryOnDpChangeFunction],
    ['update_task_summary_on_mt_change', updateTaskSummaryOnMtChangeFunction],
    ['update_task_summary_on_mta_change', updateTaskSummaryOnMtaChangeFunction],
    ['update_task_summary_on_avatar_change', updateTaskSummaryOnAvatarChangeFunction],
];
// Compute ID triggers
function computeIDTrigger(tableName) {
    return `CREATE TRIGGER ${tableName}_compute_id BEFORE INSERT ON ${tableName} FOR EACH ROW EXECUTE PROCEDURE compute_id();`;
}
// Other triggers from user request
const datapointDeleteDatasetSummaryTrigger = 'CREATE TRIGGER datapoint_delete_dataset_summary AFTER DELETE ON public.datapoint REFERENCING OLD TABLE AS old FOR EACH STATEMENT EXECUTE FUNCTION update_dataset_summary_on_dp_change();';
const datapointInsertDatasetSummaryTrigger = 'CREATE TRIGGER datapoint_insert_dataset_summary AFTER INSERT ON public.datapoint REFERENCING NEW TABLE AS new FOR EACH STATEMENT EXECUTE FUNCTION update_dataset_summary_on_dp_change();';
const microtaskDeleteTaskSummaryTrigger = 'CREATE TRIGGER microtask_delete_task_summary AFTER DELETE ON public.microtask REFERENCING OLD TABLE AS old FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_mt_change();';
const microtaskInsertTaskSummaryTrigger = 'CREATE TRIGGER microtask_insert_task_summary AFTER INSERT ON public.microtask REFERENCING NEW TABLE AS new FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_mt_change();';
// microtask_assignment triggers
const mtaDeleteTaskSummaryTrigger = 'CREATE TRIGGER mta_delete_task_summary AFTER DELETE ON public.microtask_assignment REFERENCING OLD TABLE AS old FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_mta_change();';
const mtaInsertTaskSummaryTrigger = 'CREATE TRIGGER mta_insert_task_summary AFTER INSERT ON public.microtask_assignment REFERENCING NEW TABLE AS new FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_mta_change();';
const mtaUpdateTaskSummaryTrigger = 'CREATE TRIGGER mta_update_task_summary AFTER UPDATE ON public.microtask_assignment REFERENCING NEW TABLE AS new FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_mta_change();';
// avatar triggers
const avatarDeleteTaskSummaryTrigger = 'CREATE TRIGGER avatar_delete_task_summary AFTER DELETE ON public.avatar REFERENCING OLD TABLE AS old FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_avatar_change();';
const avatarInsertTaskSummaryTrigger = 'CREATE TRIGGER avatar_insert_task_summary AFTER INSERT ON public.avatar REFERENCING NEW TABLE AS new FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_avatar_change();';
const avatarUpdateTaskSummaryTrigger = 'CREATE TRIGGER avatar_update_task_summary AFTER UPDATE ON public.avatar REFERENCING NEW TABLE AS new FOR EACH STATEMENT EXECUTE FUNCTION update_task_summary_on_avatar_change();';
// Add triggers to the schema
karyaDb.tables['avatar'].triggers = [
    computeIDTrigger('avatar'),
    avatarDeleteTaskSummaryTrigger,
    avatarInsertTaskSummaryTrigger,
    avatarUpdateTaskSummaryTrigger,
];
karyaDb.tables['datapoint'].triggers = [datapointDeleteDatasetSummaryTrigger, datapointInsertDatasetSummaryTrigger];
karyaDb.tables['microtask'].triggers = [microtaskDeleteTaskSummaryTrigger, microtaskInsertTaskSummaryTrigger];
karyaDb.tables['microtask_assignment'].triggers = [
    mtaDeleteTaskSummaryTrigger,
    mtaInsertTaskSummaryTrigger,
    mtaUpdateTaskSummaryTrigger,
];
//# sourceMappingURL=KaryaDb.js.map